import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { sanitizeReturnPath } from '@/lib/sanitizeReturnPath'

/**
 * Location state shape used by ProtectedRoute when redirecting unauthenticated
 * users to /login. `from` carries the original path so we can return after login.
 */
interface AuthRedirectState {
  from?: { pathname?: string; search?: string; hash?: string }
}

export function AuthLayout() {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (user) {
    // Honor the return path set by ProtectedRoute when the user was redirected
    // from a protected route. Sanitization blocks open-redirect attacks via
    // crafted location state. Falls back to /banking/dashboard otherwise
    // (the app's real landing page; the old /dashboard stub was removed in
    // DCP-235).
    const state = location.state as AuthRedirectState | null
    const pathname = state?.from?.pathname
    // Skip empty strings and bare "/" — the app's root redirect already
    // routes "/" to /banking/dashboard, so forwarding "/" here would chain
    // two navigations after login.
    const fromPath = pathname && pathname !== '/'
      ? `${pathname}${state?.from?.search ?? ''}${state?.from?.hash ?? ''}`
      : undefined
    const target = sanitizeReturnPath(fromPath) ?? '/banking/dashboard'
    return <Navigate to={target} replace />
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <div className="w-full max-w-md">
        <Outlet />
      </div>
    </div>
  )
}